import { useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { Building2, Search, Users, Calendar, ExternalLink } from "lucide-react";
import { format } from "date-fns";

import { useAuth } from "@/hooks/useAuth";
import { useAdminTenants, AdminTenantRow } from "@/hooks/useAdminTenants";

import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { useIsMobile } from "@/hooks/use-mobile";

const STATUS_OPTIONS = ["all", "active", "suspended", "pending"] as const;

function getStatusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "active":
      return "default";
    case "suspended":
      return "destructive";
    case "pending":
      return "secondary";
    default:
      return "outline";
  }
}

export default function TenantsPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const { data: tenants, isLoading, error } = useAdminTenants();
  const isMobile = useIsMobile();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [agencyFilter, setAgencyFilter] = useState<string>("all");
  const [selectedTenant, setSelectedTenant] = useState<AdminTenantRow | null>(null);

  // Extract unique agencies for filter dropdown
  const agencies = useMemo(() => {
    if (!tenants) return [];
    const uniqueAgencies = new Map<string, string>();
    tenants.forEach((t) => {
      if (t.agency_id && t.agency_email) {
        uniqueAgencies.set(t.agency_id, t.agency_email);
      }
    });
    return Array.from(uniqueAgencies, ([id, email]) => ({ id, email }));
  }, [tenants]);

  // Filter tenants
  const filteredTenants = useMemo(() => {
    if (!tenants) return [];
    return tenants.filter((t) => {
      const matchesSearch =
        search === "" ||
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.slug.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === "all" || t.status === statusFilter;
      const matchesAgency = agencyFilter === "all" || t.agency_id === agencyFilter;
      return matchesSearch && matchesStatus && matchesAgency;
    });
  }, [tenants, search, statusFilter, agencyFilter]);

  // Access control: only platform admins
  if (authLoading) {
    return (
      <div className="container py-6">
        <TableSkeleton rows={8} columns={7} />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (error) {
    return (
      <div className="container py-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Error Loading Tenants</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Building2 className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Tenant Overview</h1>
          <p className="text-muted-foreground">
            View all tenants across all agencies (read-only)
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={agencyFilter} onValueChange={setAgencyFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Filter by agency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Agencies</SelectItem>
            {agencies.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {s === "all" ? "All Statuses" : s.charAt(0).toUpperCase() + s.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      <p className="text-sm text-muted-foreground">
        Showing {filteredTenants.length} of {tenants?.length || 0} tenants
      </p>

      {/* Loading state */}
      {isLoading && <TableSkeleton rows={8} columns={7} />}

      {/* Desktop Table */}
      {!isLoading && !isMobile && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant Name</TableHead>
                <TableHead>Agency</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No tenants found
                  </TableCell>
                </TableRow>
              ) : (
                filteredTenants.map((tenant) => (
                  <TableRow
                    key={tenant.tenant_id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedTenant(tenant)}
                  >
                    <TableCell className="font-medium">{tenant.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {tenant.agency_email || "—"}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{tenant.slug}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(tenant.status)}>{tenant.status}</Badge>
                    </TableCell>
                    <TableCell>{tenant.plan_id || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3 text-muted-foreground" />
                        {tenant.user_count}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(tenant.created_at), "MMM d, yyyy")}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Mobile Card View */}
      {!isLoading && isMobile && (
        <div className="space-y-3">
          {filteredTenants.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No tenants found
              </CardContent>
            </Card>
          ) : (
            filteredTenants.map((tenant) => (
              <Card
                key={tenant.tenant_id}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setSelectedTenant(tenant)}
              >
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{tenant.name}</p>
                      <p className="text-sm text-muted-foreground font-mono">{tenant.slug}</p>
                    </div>
                    <Badge variant={getStatusVariant(tenant.status)}>{tenant.status}</Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {tenant.user_count} users
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(tenant.created_at), "MMM d, yyyy")}
                    </span>
                  </div>
                  {tenant.agency_email && (
                    <p className="text-sm text-muted-foreground">Agency: {tenant.agency_email}</p>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Detail Sheet (read-only) */}
      <Sheet open={!!selectedTenant} onOpenChange={() => setSelectedTenant(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {selectedTenant?.name}
            </SheetTitle>
            <SheetDescription>Tenant details (read-only)</SheetDescription>
          </SheetHeader>

          {selectedTenant && (
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Tenant ID</p>
                  <p className="font-mono text-sm break-all">{selectedTenant.tenant_id}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Slug</p>
                  <p className="font-mono text-sm">{selectedTenant.slug}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <Badge variant={getStatusVariant(selectedTenant.status)} className="mt-1">
                    {selectedTenant.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Plan</p>
                  <p>{selectedTenant.plan_id || "—"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Users</p>
                  <p className="flex items-center gap-1">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    {selectedTenant.user_count}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Created</p>
                  <p>{format(new Date(selectedTenant.created_at), "PPP")}</p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-muted-foreground mb-2">Agency</p>
                {selectedTenant.agency_email ? (
                  <div className="flex items-center gap-2">
                    <p>{selectedTenant.agency_email}</p>
                    <ExternalLink className="h-3 w-3 text-muted-foreground" />
                  </div>
                ) : (
                  <p className="text-muted-foreground">No agency assigned</p>
                )}
                {selectedTenant.agency_id && (
                  <p className="text-xs text-muted-foreground font-mono mt-1">
                    {selectedTenant.agency_id}
                  </p>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
